// src/components/communication/CommunicationDrawer.js
import React, { useState, useRef, useEffect } from "react";
import {
  X,
  MessageSquare,
  Bell,
  Calendar,
  Send,
  Paperclip,
  Image as ImageIcon,
  Plus,
  Check,
  CheckCheck,
  Trash2,
  ExternalLink,
  ChevronRight,
  User,
  Wrench,
  Factory,
  Truck,
  Mail,
  ShoppingBag,
  Megaphone,
  AtSign,
  Clock,
  Flag,
  AlertCircle,
  CheckCircle2,
  Circle,
  FileText,
  Search,
  Sparkles,
  RefreshCw,
  Layers,
  HelpCircle,
  BookOpen,
  Keyboard,
  Zap,
  Gem,
  Package,
  Copy,
  Settings,
  GraduationCap,
  Users,
  Volume2,
  VolumeX,
} from "lucide-react";
import { pb } from "../../services/pocketbase";
import { useCommunication } from "../../contexts/CommunicationContext";
import { useAuth } from "../../contexts/AuthContext";
import { useOperator } from "../../contexts/OperatorContext";
import MessageContentParser from "./MessageContentParser";
import MentionAutocomplete from "./MentionAutocomplete";
import ChatInputBar from "./ChatInputBar";
import { aiAssistantService } from "../../services/aiAssistantService";

const CHANNEL_ICONS = {
  Megaphone: Megaphone,
  Factory: Factory,
  Wrench: Wrench,
  Truck: Truck,
  ShoppingBag: ShoppingBag,
  Mail: Mail,
  User: User,
  Layers: Layers,
};

const MODULE_OPTIONS = [
  { value: "", label: "Geral (Público para todos)" },
  { value: "production", label: "Produção & Fábrica" },
  { value: "oficina", label: "Oficina & Bancada" },
  { value: "orders", label: "Pedidos & Logística" },
  { value: "pdv", label: "PDV Showroom" },
  { value: "postagem", label: "Expedição Correios" },
  { value: "colaboradores", label: "Colaboradores & RH" },
  { value: "stock", label: "Estoque" },
  { value: "financeiro", label: "Financeiro" },
];

const PRIORITY_BADGES = {
  urgente: { label: "Urgente", color: "bg-rose-100 text-rose-700 border-rose-200" },
  alta: { label: "Alta", color: "bg-orange-100 text-orange-700 border-orange-200" },
  media: { label: "Média", color: "bg-amber-100 text-amber-700 border-amber-200" },
  baixa: { label: "Baixa", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
};

// BALÃO DE MENSAGEM MEMOIZADO (Evita re-renderizações durante a digitação)
const ChatMessageBubble = React.memo(({ msg, isMe, isAI, timeStr }) => {
  return (
    <div className={`flex flex-col ${isMe ? "items-end" : "items-start"} animate-fadeIn`}>
      {!isMe && (
        <div className="flex items-center gap-1.5 mb-1 px-1">
          {isAI && <Sparkles size={12} className="text-indigo-600" />}
          <span
            className="text-[11px] font-bold"
            style={{ color: isAI ? "#4f46e5" : msg.sender_color || "#3b82f6" }}
          >
            {msg.sender_name}
          </span>
          {isAI && (
            <span className="text-[9px] font-bold bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full border border-indigo-200">
              IA Companion
            </span>
          )}
          <span className="text-[10px] text-slate-400">{timeStr}</span>
        </div>
      )}

      <div
        className={`max-w-[88%] rounded-2xl px-4 py-3 shadow-xs ${
          isMe
            ? "bg-indigo-600 text-white rounded-tr-xs border border-indigo-700/20"
            : isAI
            ? "bg-gradient-to-br from-indigo-50/90 via-purple-50/40 to-white text-slate-800 rounded-tl-xs border border-indigo-200/90 shadow-xs"
            : "bg-white border border-slate-200 text-slate-800 rounded-tl-xs"
        }`}
      >
        <MessageContentParser message={msg} isMe={isMe} />

        {isMe && (
          <div className="text-[9px] text-indigo-100 text-right mt-1.5 flex items-center justify-end gap-1 font-medium">
            <span>{timeStr}</span>
            <CheckCheck size={12} className="text-indigo-200" />
          </div>
        )}
      </div>
    </div>
  );
});

export default function CommunicationDrawer({ onNavigateTab }) {
  const { user } = useAuth();
  const { activeOperator } = useOperator();
  const {
    drawerOpen,
    closeDrawer,
    activeDrawerTab,
    setActiveDrawerTab,
    unreadNotifications,
    unreadChatCount,
    unreadByChannel,
    markChannelAsRead,
    markAllChannelsAsRead,
    pendingTasksCount,
    channels,
    dms,
    activeChannel,
    setActiveChannel,
    messages,
    loadingMessages,
    sendMessage,
    startDM,
    startAIChat,
    isAiThinking,
    createChannel,
    seedChannels,
    notifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    tasks,
    updateTaskStatus,
    deleteTask,
    setNewTaskModalOpen,
    usersList,
    operatorsList,
    teamsList,
    openQuickOrder,
    openQuickProduct,
    selectedAiProviderId,
    setSelectedAiProviderId,
    clearCurrentChannelMessages,
    selectChannelById,
    soundEnabled,
    toggleSound,
    testSound,
  } = useCommunication();

  const getDMDisplayName = (dm) => {
    if (!dm) return "";
    if (dm.type === "ai") return "Assistente IA ✨";

    const currentUserId = user?.id;
    const currentOpId = activeOperator?.id;

    if (dm.expand?.members_users && dm.expand.members_users.length > 0) {
      const otherUser = dm.expand.members_users.find((u) => u.id !== currentUserId);
      if (otherUser) return otherUser.name || otherUser.username || otherUser.email;
    }

    if (dm.expand?.members_operators && dm.expand.members_operators.length > 0) {
      const otherOp = dm.expand.members_operators.find((op) => op.id !== currentOpId);
      if (otherOp) return `${otherOp.nome} (Operador)`;
    }

    if (dm.members_users && Array.isArray(dm.members_users)) {
      const otherUserId = dm.members_users.find((id) => id !== currentUserId);
      if (otherUserId && usersList) {
        const foundUser = usersList.find((u) => u.id === otherUserId);
        if (foundUser) return foundUser.name || foundUser.username;
      }
    }

    if (dm.members_operators && Array.isArray(dm.members_operators)) {
      const otherOpId = dm.members_operators.find((id) => id !== currentOpId);
      if (otherOpId && operatorsList) {
        const foundOp = operatorsList.find((op) => op.id === otherOpId);
        if (foundOp) return `${foundOp.nome} (Operador)`;
      }
    }

    return dm.display_name || "Conversa Direta";
  };

  const [availableAiProviders, setAvailableAiProviders] = useState([]);
  const [savingProvider, setSavingProvider] = useState(false);

  useEffect(() => {
    aiAssistantService.getAvailableProviders().then((list) => {
      setAvailableAiProviders(list);
      if (list.length > 0) {
        const openai = list.find((p) => p.provedor === "openai" && p.temChave);
        const active = list.find((p) => p.ativo && p.temChave);
        if (openai) setSelectedAiProviderId(openai.id);
        else if (active) setSelectedAiProviderId(active.id);
        else setSelectedAiProviderId(list[0].id);
      }
    });
  }, []); // Executa apenas 1 vez na montagem inicial

  const handleSetDefaultAiProvider = async (providerId) => {
    setSavingProvider(true);
    setSelectedAiProviderId(providerId);
    try {
      const list = await pb.collection("ia_configs").getFullList();
      for (const item of list) {
        const isSelected = item.id === providerId;
        if (item.ativo !== isSelected) {
          await pb.collection("ia_configs").update(item.id, { ativo: isSelected }).catch(() => {});
        }
      }
      const updatedList = await aiAssistantService.getAvailableProviders();
      setAvailableAiProviders(updatedList);
    } catch (e) {
      console.warn("Erro ao definir provedor padrão:", e);
    } finally {
      setSavingProvider(false);
    }
  };

  // Estados locais de Chat
  const [suggestionPrompt, setSuggestionPrompt] = useState("");
  const [seeding, setSeeding] = useState(false);
  const [showDMSelector, setShowDMSelector] = useState(false);
  const [dmSearch, setDmSearch] = useState("");

  // Modal de Criar Canal
  const [showNewChannelModal, setShowNewChannelModal] = useState(false);
  const [newChannelDisplayName, setNewChannelDisplayName] = useState("");
  const [newChannelModule, setNewChannelModule] = useState("");
  const [newChannelDesc, setNewChannelDesc] = useState("");
  const [newChannelIcon, setNewChannelIcon] = useState("Megaphone");
  const [creatingChannel, setCreatingChannel] = useState(false);

  // Estados locais de Notificações
  const [notifFilter, setNotifFilter] = useState("all"); // "all" | "unread"

  // Estados locais de Tarefas
  const [taskFilter, setTaskFilter] = useState("pendente"); // "pendente" | "concluida" | "all"

  // Refs
  const messagesEndRef = useRef(null);

  // Auto-scroll para última mensagem
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (activeDrawerTab === "chat" && messages.length > 0) {
      scrollToBottom();
    }
  }, [messages, activeDrawerTab]);

  // Se houver canais e nenhum ativo, seleciona o primeiro
  useEffect(() => {
    if (!activeChannel && channels.length > 0) {
      setActiveChannel(channels[0]);
    }
  }, [channels, activeChannel, setActiveChannel]);

  // Handler para Seed Manual de Canais Padrão
  const handleSeedChannels = async () => {
    setSeeding(true);
    try {
      await seedChannels();
    } catch (err) {
      console.error("Erro ao seedar canais:", err);
    } finally {
      setSeeding(false);
    }
  };

  // Handler para Criar Canal Customizado
  const handleCreateChannelSubmit = async (e) => {
    e.preventDefault();
    if (!newChannelDisplayName.trim()) return;

    setCreatingChannel(true);
    try {
      await createChannel({
        display_name: newChannelDisplayName.trim(),
        module_key: newChannelModule,
        description: newChannelDesc.trim(),
        icon: newChannelIcon,
      });

      setNewChannelDisplayName("");
      setNewChannelModule("");
      setNewChannelDesc("");
      setShowNewChannelModal(false);
    } catch (err) {
      alert("Erro ao criar canal: " + (err?.data?.message || err?.message || "Verifique os dados"));
    } finally {
      setCreatingChannel(false);
    }
  };

  if (!drawerOpen) return null;

  return (
    <div
      onClick={closeDrawer}
      className="fixed inset-0 z-[9900] flex justify-end bg-slate-900/40 backdrop-blur-xs animate-fadeIn cursor-pointer"
    >
      <div
        className="w-full max-w-xl md:max-w-2xl bg-white h-full shadow-2xl flex flex-col border-l border-slate-200 animate-slideInRight cursor-default"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ================= 1. HEADER DO DRAWER ================= */}
        <div className="px-5 py-3.5 bg-slate-900 text-white flex items-center justify-between shrink-0">
          {/* TABS PRINCIPAIS */}
          <div className="flex items-center gap-1 p-1 bg-slate-800/80 rounded-xl border border-slate-700">
            {/* 1. Conversas */}
            <button
              onClick={() => setActiveDrawerTab("chat")}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                activeDrawerTab === "chat"
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              <MessageSquare size={13} />
              <span>Conversas</span>
              {unreadChatCount > 0 && (
                <span className="min-w-[16px] h-4 px-1 rounded-full bg-rose-600 text-white text-[10px] flex items-center justify-center font-black shadow-xs">
                  {unreadChatCount}
                </span>
              )}
            </button>

            {/* 2. Avisos */}
            <button
              onClick={() => setActiveDrawerTab("notifications")}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer relative ${
                activeDrawerTab === "notifications"
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              <Bell size={13} />
              <span>Avisos</span>
              {unreadNotifications > 0 && (
                <span className="px-1.5 py-0.2 rounded-full bg-rose-500 text-white text-[10px] font-bold">
                  {unreadNotifications}
                </span>
              )}
            </button>

            {/* 3. Tarefas */}
            <button
              onClick={() => setActiveDrawerTab("tasks")}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                activeDrawerTab === "tasks"
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              <Calendar size={13} />
              <span>Tarefas</span>
              {pendingTasksCount > 0 && (
                <span className="px-1.5 py-0.2 rounded-full bg-amber-500 text-slate-950 text-[10px] font-bold">
                  {pendingTasksCount}
                </span>
              )}
            </button>

            {/* 4. AJUDA */}
            <button
              onClick={() => setActiveDrawerTab("help")}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                activeDrawerTab === "help"
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              <HelpCircle size={13} />
              <span>Ajuda</span>
            </button>

            {/* 5. PREFERÊNCIAS */}
            <button
              onClick={() => setActiveDrawerTab("preferences")}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                activeDrawerTab === "preferences"
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              <Settings size={13} />
              <span>Preferências</span>
            </button>
          </div>

          <div className="flex items-center gap-1.5">
            {/* BOTÃO DE SOM (ATIVAR / SILENCIAR) */}
            <button
              type="button"
              onClick={toggleSound}
              className={`p-1.5 rounded-lg transition flex items-center justify-center cursor-pointer ${
                soundEnabled
                  ? "text-emerald-400 hover:text-emerald-300 hover:bg-white/10"
                  : "text-slate-500 hover:text-slate-400 hover:bg-white/10"
              }`}
              title={soundEnabled ? "Sons de alerta ativados (clique para silenciar)" : "Sons silenciados (clique para ativar)"}
            >
              {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
            </button>

            <button
              onClick={closeDrawer}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition cursor-pointer"
              title="Fechar Gaveta"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* ================= 2. CONTEÚDO DA TAB ATIVA ================= */}
        {activeDrawerTab === "chat" && (
          <div className="flex-1 flex overflow-hidden">
            {/* SUB-BARRA LATERAL DE CANAIS & DMS (ESQUERDA) */}
            <div className="w-52 border-r border-slate-200 bg-slate-50 flex flex-col shrink-0">
              <div className="p-3 border-b border-slate-200/80 flex items-center justify-between">
                <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                  Canais por Área
                </span>
                <div className="flex items-center gap-1">
                  {unreadChatCount > 0 && (
                    <button
                      onClick={markAllChannelsAsRead}
                      className="p-1 rounded-md text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition cursor-pointer"
                      title="Marcar todas as conversas como lidas"
                    >
                      <CheckCheck size={14} />
                    </button>
                  )}
                  <button
                    onClick={() => setShowNewChannelModal(true)}
                    className="p-1 rounded-md text-indigo-600 hover:bg-indigo-50 transition cursor-pointer"
                    title="Criar Novo Canal"
                  >
                    <Plus size={14} />
                  </button>
                </div>
              </div>

              {/* LISTA DE CANAIS */}
              <div className="flex-1 overflow-y-auto p-2 space-y-1">
                {channels.map((ch) => {
                  const IconComp = CHANNEL_ICONS[ch.icon] || Megaphone;
                  const isSelected = activeChannel?.id === ch.id;
                  const unreadCount = unreadByChannel?.[ch.id] || 0;

                  return (
                    <button
                      key={ch.id}
                      onClick={() => {
                        setActiveChannel(ch);
                        markChannelAsRead(ch.id);
                      }}
                      className={`w-full px-2.5 py-2 rounded-xl text-left flex items-center gap-2 text-xs font-bold transition cursor-pointer relative ${
                        isSelected
                          ? "bg-indigo-600 text-white shadow-sm"
                          : unreadCount > 0
                          ? "bg-rose-50 text-rose-950 font-black border border-rose-200/90 shadow-2xs"
                          : "text-slate-700 hover:bg-slate-200/70"
                      }`}
                    >
                      <div className="relative shrink-0">
                        <IconComp size={14} className={isSelected ? "text-white" : unreadCount > 0 ? "text-rose-600" : "text-slate-500"} />
                        {unreadCount > 0 && !isSelected && (
                          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-rose-600 ring-2 ring-white shadow-xs" />
                        )}
                      </div>
                      <span className="truncate flex-1">{ch.display_name}</span>
                      {unreadCount > 0 && !isSelected && (
                        <span className="min-w-[18px] h-[18px] px-1.5 rounded-full bg-rose-600 text-white text-[10px] font-black shrink-0 shadow-xs flex items-center justify-center">
                          {unreadCount > 99 ? "99+" : unreadCount}
                        </span>
                      )}
                    </button>
                  );
                })}

                {channels.length === 0 && (
                  <div className="p-2 text-center space-y-2">
                    <p className="text-[11px] text-slate-400">Nenhum canal cadastrado.</p>
                    <button
                      onClick={handleSeedChannels}
                      disabled={seeding}
                      className="w-full py-1.5 px-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-[11px] font-bold rounded-lg border border-indigo-200 transition cursor-pointer"
                    >
                      {seeding ? "Criando..." : "✨ Gerar Canais Padrão"}
                    </button>
                  </div>
                )}

                {/* SEÇÃO DE MENSAGENS DIRETAS (DMS) */}
                <div className="pt-3 pb-1 px-1 flex items-center justify-between border-t border-slate-200/80 mt-2">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    Mensagens Diretas
                  </span>
                  <button
                    onClick={() => setShowDMSelector(true)}
                    className="p-1 rounded-md text-indigo-600 hover:bg-indigo-50 transition cursor-pointer"
                    title="Nova Mensagem Direta"
                  >
                    <Plus size={13} />
                  </button>
                </div>

                {/* BOTÃO FIXO DO ASSISTENTE COMPANION DE IA */}
                <button
                  onClick={startAIChat}
                  className={`w-full px-2.5 py-2 rounded-xl text-left flex items-center gap-2 text-xs font-bold transition cursor-pointer mb-1.5 ${
                    activeChannel?.type === "ai"
                      ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-sm"
                      : "bg-indigo-50/90 hover:bg-indigo-100 text-indigo-900 border border-indigo-200/80 shadow-2xs"
                  }`}
                >
                  <div className="w-5 h-5 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-[10px] font-bold shadow-xs">
                    <Sparkles size={11} />
                  </div>
                  <span className="truncate flex-1">Assistente IA ✨</span>
                </button>

                {dms.filter((dm) => dm.type === "direct").map((dm) => {
                  const isSelected = activeChannel?.id === dm.id;
                  const label = getDMDisplayName(dm);
                  const unreadCount = unreadByChannel?.[dm.id] || 0;

                  return (
                    <button
                      key={dm.id}
                      onClick={() => {
                        setActiveChannel(dm);
                        markChannelAsRead(dm.id);
                      }}
                      className={`w-full px-2.5 py-2 rounded-xl text-left flex items-center gap-2 text-xs font-semibold transition cursor-pointer relative ${
                        isSelected
                          ? "bg-indigo-600 text-white shadow-sm"
                          : unreadCount > 0
                          ? "bg-rose-50 text-rose-950 font-bold border border-rose-200/90 shadow-2xs"
                          : "text-slate-700 hover:bg-slate-200/70"
                      }`}
                    >
                      <div className="relative shrink-0">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
                          unreadCount > 0 && !isSelected ? "bg-rose-100 text-rose-800" : "bg-slate-300 text-slate-700"
                        }`}>
                          <User size={11} />
                        </div>
                        {unreadCount > 0 && !isSelected && (
                          <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-rose-600 ring-2 ring-white shadow-xs" />
                        )}
                      </div>
                      <span className="truncate flex-1">{label}</span>
                      {unreadCount > 0 && !isSelected && (
                        <span className="min-w-[18px] h-[18px] px-1.5 rounded-full bg-rose-600 text-white text-[10px] font-black shrink-0 shadow-xs flex items-center justify-center">
                          {unreadCount > 99 ? "99+" : unreadCount}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* RODAPÉ DO OPERADOR/USUÁRIO LOGADO */}
              <div className="p-2.5 bg-slate-100/90 border-t border-slate-200 text-[11px] flex items-center gap-2">
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold"
                  style={{ backgroundColor: activeOperator?.cor || "#3b82f6" }}
                >
                  {activeOperator ? <Wrench size={11} /> : <User size={11} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-800 truncate">
                    {activeOperator ? activeOperator.nome : user?.name || user?.username}
                  </p>
                  <span className="text-[10px] text-slate-500 block truncate">
                    {activeOperator ? "Operador Ativo" : user?.role || "Usuário"}
                  </span>
                </div>
              </div>
            </div>

            {/* ÁREA PRINCIPAL DE MENSAGENS (DIREITA) */}
            <div className="flex-1 flex flex-col bg-white overflow-hidden">
              {/* HEADER DO CANAL ATIVO */}
              {activeChannel ? (
                <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between shrink-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-xs text-slate-800">
                      {activeChannel.type === "direct"
                        ? `👤 ${getDMDisplayName(activeChannel)}`
                        : activeChannel.type === "ai"
                        ? "✨ Assistente IA"
                        : `# ${activeChannel.display_name || activeChannel.name}`}
                    </span>
                    {activeChannel.description && (
                      <span className="text-[11px] text-slate-400 hidden sm:inline">
                        — {activeChannel.description}
                      </span>
                    )}
                  </div>

                  {/* CONTROLES DO CANAL DA IA (LIMPAR CONVERSA) */}
                  {activeChannel.type === "ai" && (
                    <button
                      type="button"
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (window.confirm("Deseja realmente limpar as mensagens desta conversa com o Assistente de IA?")) {
                          await clearCurrentChannelMessages();
                        }
                      }}
                      className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold text-slate-600 hover:text-red-600 hover:bg-red-50 border border-slate-200 shadow-2xs transition cursor-pointer"
                      title="Limpar histórico da conversa com a IA"
                    >
                      <Trash2 size={13} className="text-slate-400 group-hover:text-red-500" />
                      <span>Limpar Chat</span>
                    </button>
                  )}
                </div>
              ) : (
                <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500">
                  Nenhum canal ativo
                </div>
              )}

              {/* FEED DE MENSAGENS */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50/50">
                {loadingMessages && messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-xs text-slate-400">
                    Carregando mensagens...
                  </div>
                ) : !activeChannel ? (
                  <div className="flex flex-col items-center justify-center h-full text-center text-slate-400 p-6 space-y-3">
                    <Megaphone size={36} className="text-slate-300" />
                    <div>
                      <p className="text-sm font-bold text-slate-700">Bem-vindo ao Chat Interno</p>
                      <p className="text-xs text-slate-500 max-w-sm mt-1">
                        Selecione um canal à esquerda ou gere os canais padrão para começar a conversar com a equipe.
                      </p>
                    </div>
                    <div className="flex items-center gap-2 pt-2">
                      <button
                        onClick={handleSeedChannels}
                        disabled={seeding}
                        className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold shadow-sm transition cursor-pointer"
                      >
                        {seeding ? "Criando Canais..." : "✨ Gerar Canais Padrão"}
                      </button>
                      <button
                        onClick={() => setShowNewChannelModal(true)}
                        className="px-3.5 py-2 rounded-xl border border-slate-300 hover:bg-slate-100 text-slate-700 text-xs font-semibold transition cursor-pointer"
                      >
                        + Criar Canal
                      </button>
                    </div>
                  </div>
                ) : messages.length === 0 ? (
                  activeChannel.type === "ai" ? (
                    <div className="p-4 rounded-2xl bg-gradient-to-br from-indigo-50/90 to-purple-50/70 border border-indigo-200/80 space-y-3 text-slate-800 animate-fadeIn">
                      <div className="flex items-center gap-2 font-bold text-xs text-indigo-950">
                        <Sparkles size={16} className="text-indigo-600" />
                        <span>Assistente Companion da Sempre Joias</span>
                      </div>
                      <p className="text-xs text-slate-600 leading-relaxed">
                        Olá! Sou o assistente inteligente da fábrica. Posso consultar dados em tempo real no banco sem sobrecarregar o sistema. Experimente me perguntar:
                      </p>
                      <div className="space-y-1.5 text-xs">
                        <div
                          onClick={() => setSuggestionPrompt("Como está a produção do pedido [71740] e quais os aros?")}
                          className="p-2.5 rounded-xl bg-white border border-indigo-100 hover:border-indigo-300 text-slate-700 cursor-pointer transition shadow-2xs"
                        >
                          💬 <b>"Como está a produção do pedido [71740] e quais os aros?"</b>
                        </div>
                        <div
                          onClick={() => setSuggestionPrompt("Qual o preço e categoria do produto sku.DIR-TRI?")}
                          className="p-2.5 rounded-xl bg-white border border-indigo-100 hover:border-indigo-300 text-slate-700 cursor-pointer transition shadow-2xs"
                        >
                          💎 <b>"Qual o preço e categoria do produto sku.DIR-TRI?"</b>
                        </div>
                        <div
                          onClick={() => setSuggestionPrompt("Quais tarefas de prioridade urgente temos pendentes hoje?")}
                          className="p-2.5 rounded-xl bg-white border border-indigo-100 hover:border-indigo-300 text-slate-700 cursor-pointer transition shadow-2xs"
                        >
                          📅 <b>"Quais tarefas de prioridade urgente temos pendentes hoje?"</b>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-center text-slate-400 p-6 space-y-2">
                      <MessageSquare size={32} className="text-slate-300" />
                      <p className="text-xs font-semibold">Nenhuma mensagem em {activeChannel.display_name}.</p>
                      <p className="text-[11px]">
                        Envie uma mensagem, mencione pessoas com @, linke pedidos com [número] ou produtos com sku.CODIGO.
                      </p>
                    </div>
                  )
                ) : (
                  messages.map((msg) => {
                    const isMe =
                      (user?.id && msg.sender_user === user.id && !activeOperator) ||
                      (activeOperator?.id && msg.sender_operator === activeOperator.id);

                    const isAI = msg.sender_name?.includes("Assistente") || msg.sender_color === "#6366f1";

                    const timeStr = msg.created
                      ? new Date(msg.created).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                      : "";

                    return (
                      <ChatMessageBubble
                        key={msg.id}
                        msg={msg}
                        isMe={isMe}
                        isAI={isAI}
                        timeStr={timeStr}
                      />
                    );
                  })
                )}

                {/* INDICADOR DE PROCESSAMENTO DA IA */}
                {isAiThinking && (
                  <div className="flex items-start gap-2 animate-pulse my-2">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-xs shadow-xs shrink-0">
                      <Sparkles size={12} className="animate-spin" />
                    </div>
                    <div className="bg-white border border-indigo-200 rounded-2xl rounded-tl-xs px-3.5 py-2.5 text-xs text-indigo-900 shadow-xs flex items-center gap-2">
                      <span>Assistente está consultando o sistema e pensando...</span>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* CAIXA DE ENTRADA DO CHAT (COMPONENTE ISOLADO DE ALTA PERFORMANCE) */}
              <ChatInputBar
                activeChannel={activeChannel}
                usersList={usersList}
                operatorsList={operatorsList}
                teamsList={teamsList}
                onSendMessage={sendMessage}
                initialText={suggestionPrompt}
                onClearInitialText={() => setSuggestionPrompt("")}
              />
            </div>
          </div>
        )}

        {/* ================= 3. CONTEÚDO DA TAB NOTIFICAÇÕES ================= */}
        {activeDrawerTab === "notifications" && (
          <div className="flex-1 flex flex-col bg-slate-50 overflow-hidden">
            {/* BARRA DE FILTROS E AÇÕES */}
            <div className="p-4 bg-white border-b border-slate-200 flex items-center justify-between">
              <div className="flex rounded-xl bg-slate-100 p-0.5 text-xs font-bold">
                <button
                  onClick={() => setNotifFilter("all")}
                  className={`px-3 py-1 rounded-lg transition cursor-pointer ${
                    notifFilter === "all" ? "bg-white text-slate-800 shadow-xs" : "text-slate-500"
                  }`}
                >
                  Todas ({notifications.length})
                </button>
                <button
                  onClick={() => setNotifFilter("unread")}
                  className={`px-3 py-1 rounded-lg transition cursor-pointer ${
                    notifFilter === "unread" ? "bg-white text-slate-800 shadow-xs" : "text-slate-500"
                  }`}
                >
                  Não Lidas ({unreadNotifications})
                </button>
              </div>

              {unreadNotifications > 0 && (
                <button
                  onClick={markAllNotificationsAsRead}
                  className="text-xs font-bold text-indigo-600 hover:text-indigo-800 transition cursor-pointer"
                >
                  Marcar todas como lidas
                </button>
              )}
            </div>

            {/* LISTA DE NOTIFICAÇÕES */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
              {notifications
                .filter((n) => (notifFilter === "unread" ? !n.read : true))
                .map((notif) => {
                  const timeAgo = notif.created
                    ? new Date(notif.created).toLocaleString([], {
                        day: "2-digit",
                        month: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : "";

                  return (
                    <div
                      key={notif.id}
                      onClick={() => {
                        markNotificationAsRead(notif.id);

                        // 1. Notificação de Chat (DM ou Menção)
                        if (notif.link_tab === "chat" || notif.type === "direct_message" || notif.type === "mention") {
                          if (notif.link_id) {
                            selectChannelById(notif.link_id);
                          } else {
                            setActiveDrawerTab("chat");
                          }
                          return;
                        }

                        // 2. Notificação de Tarefa
                        if (notif.link_tab === "tasks" || notif.type === "task_assigned") {
                          setActiveDrawerTab("tasks");
                          return;
                        }

                        // 3. Notificação de Pedido de Fábrica
                        if (notif.link_tab === "orders" || notif.type === "order") {
                          if (notif.link_id) openQuickOrder(notif.link_id);
                          if (onNavigateTab) onNavigateTab("orders");
                          return;
                        }

                        // 4. Navegação geral
                        if (notif.link_tab && onNavigateTab) {
                          onNavigateTab(notif.link_tab);
                        }
                      }}
                      className={`p-3.5 rounded-2xl border transition cursor-pointer flex items-start gap-3 ${
                        notif.read
                          ? "bg-white border-slate-200 opacity-80"
                          : "bg-white border-indigo-200 shadow-sm ring-1 ring-indigo-500/10"
                      }`}
                    >
                      <div
                        className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                          notif.type === "mention"
                            ? "bg-blue-50 text-blue-600"
                            : notif.type === "task_assigned"
                            ? "bg-amber-50 text-amber-600"
                            : "bg-indigo-50 text-indigo-600"
                        }`}
                      >
                        {notif.type === "mention" ? (
                          <AtSign size={16} />
                        ) : notif.type === "task_assigned" ? (
                          <Calendar size={16} />
                        ) : (
                          <Bell size={16} />
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <h4 className="text-xs font-bold text-slate-800 truncate">{notif.title}</h4>
                          <span className="text-[10px] text-slate-400 shrink-0">{timeAgo}</span>
                        </div>
                        <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{notif.message}</p>
                      </div>

                      {!notif.read && (
                        <div className="w-2 h-2 rounded-full bg-indigo-600 shrink-0 mt-1" />
                      )}
                    </div>
                  );
                })}

              {notifications.length === 0 && (
                <div className="flex flex-col items-center justify-center h-48 text-center text-slate-400">
                  <Bell size={32} className="text-slate-300 mb-2" />
                  <p className="text-xs font-semibold">Tudo em dia!</p>
                  <p className="text-[11px]">Você não tem novas notificações no momento.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ================= 4. CONTEÚDO DA TAB TAREFAS & PRAZOS ================= */}
        {activeDrawerTab === "tasks" && (
          <div className="flex-1 flex flex-col bg-slate-50 overflow-hidden">
            {/* HEADER COM FILTROS E BOTÃO DE NOVA TAREFA */}
            <div className="p-4 bg-white border-b border-slate-200 flex items-center justify-between">
              <div className="flex rounded-xl bg-slate-100 p-0.5 text-xs font-bold">
                <button
                  onClick={() => setTaskFilter("pendente")}
                  className={`px-3 py-1 rounded-lg transition cursor-pointer ${
                    taskFilter === "pendente" ? "bg-white text-slate-800 shadow-xs" : "text-slate-500"
                  }`}
                >
                  Pendentes
                </button>
                <button
                  onClick={() => setTaskFilter("concluida")}
                  className={`px-3 py-1 rounded-lg transition cursor-pointer ${
                    taskFilter === "concluida" ? "bg-white text-slate-800 shadow-xs" : "text-slate-500"
                  }`}
                >
                  Concluídas
                </button>
                <button
                  onClick={() => setTaskFilter("all")}
                  className={`px-3 py-1 rounded-lg transition cursor-pointer ${
                    taskFilter === "all" ? "bg-white text-slate-800 shadow-xs" : "text-slate-500"
                  }`}
                >
                  Todas
                </button>
              </div>

              <button
                onClick={() => setNewTaskModalOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold shadow-sm shadow-indigo-600/30 transition cursor-pointer"
              >
                <Plus size={13} />
                <span>Nova Tarefa</span>
              </button>
            </div>

            {/* LISTA DE TAREFAS */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {tasks
                .filter((t) => (taskFilter === "all" ? true : t.status === taskFilter))
                .map((task) => {
                  const isDone = task.status === "concluida";
                  const priorityInfo = PRIORITY_BADGES[task.priority] || PRIORITY_BADGES.media;

                  return (
                    <div
                      key={task.id}
                      className={`p-4 rounded-2xl bg-white border transition shadow-xs space-y-2 ${
                        isDone ? "border-slate-200 opacity-60" : "border-slate-200/90 hover:border-slate-300"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await updateTaskStatus(task.id, isDone ? "pendente" : "concluida");
                            } catch (err) {
                              alert("Falha ao atualizar tarefa: " + (err.message || "Verifique permissões"));
                            }
                          }}
                          className={`mt-0.5 w-5 h-5 rounded-lg border flex items-center justify-center transition shrink-0 cursor-pointer ${
                            isDone
                              ? "bg-emerald-500 border-emerald-500 text-white"
                              : "border-slate-300 hover:border-indigo-500 text-transparent"
                          }`}
                        >
                          <Check size={12} strokeWidth={3} />
                        </button>

                        <div className="flex-1 min-w-0">
                          <h4
                            className={`text-xs font-bold text-slate-800 ${
                              isDone ? "line-through text-slate-400" : ""
                            }`}
                          >
                            {task.title}
                          </h4>
                          {task.description && (
                            <p className="text-xs text-slate-500 mt-1">{task.description}</p>
                          )}
                        </div>

                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <span
                            className={`px-2 py-0.5 rounded-md text-[10px] font-bold border ${priorityInfo.color}`}
                          >
                            {priorityInfo.label}
                          </span>
                          {(task.assigned_team || task.team_name || task.module_key?.startsWith("team_")) && (
                            <span className="flex items-center gap-1 font-bold text-purple-700 bg-purple-50 border border-purple-200/80 px-1.5 py-0.5 rounded text-[10px]">
                              <Users size={10} /> {task.team_name || "Equipe"}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* RODAPÉ DO CARD DE TAREFA */}
                      <div className="flex items-center justify-between text-[11px] text-slate-500 pt-2 border-t border-slate-100">
                        <div className="flex items-center gap-3">
                          {task.order_number && (
                            <button
                              onClick={() => openQuickOrder(task.order_number)}
                              className="font-bold text-indigo-600 hover:underline flex items-center gap-1 cursor-pointer"
                            >
                              <span>Pedido #{task.order_number}</span>
                            </button>
                          )}

                          {task.due_date && (
                            <span className="flex items-center gap-1 text-slate-500">
                              <Clock size={11} />
                              {new Date(task.due_date).toLocaleDateString([], {
                                day: "2-digit",
                                month: "2-digit",
                              })}
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          {isDone && task.completed_by_name && (
                            <span className="text-[10px] text-emerald-600 font-semibold truncate max-w-[130px]" title={`Concluída por ${task.completed_by_name}`}>
                              Concluída por {task.completed_by_name}
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={async () => {
                              if (window.confirm("Deseja realmente excluir esta tarefa?")) {
                                try {
                                  await deleteTask(task.id);
                                } catch (err) {
                                  alert("Falha ao excluir tarefa: " + (err.message || "Verifique permissões"));
                                }
                              }
                            }}
                            className="text-slate-300 hover:text-rose-500 transition cursor-pointer p-1 rounded-lg hover:bg-rose-50"
                            title="Excluir Tarefa"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}

              {tasks.length === 0 && (
                <div className="flex flex-col items-center justify-center h-48 text-center text-slate-400">
                  <Calendar size={32} className="text-slate-300 mb-2" />
                  <p className="text-xs font-semibold">Nenhuma tarefa cadastrada.</p>
                  <p className="text-[11px]">Clique em "+ Nova Tarefa" para criar sua primeira tarefa ou prazo.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ================= 5. CONTEÚDO DA TAB AJUDA & GUIA RÁPIDO ================= */}
        {activeDrawerTab === "help" && (
          <div className="flex-1 overflow-y-auto p-5 bg-slate-50 space-y-5 custom-scrollbar animate-fadeIn">
            {/* HERO BANNER */}
            <div className="p-4 rounded-2xl bg-gradient-to-r from-indigo-950 via-slate-900 to-indigo-900 text-white shadow-sm space-y-1.5">
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-indigo-400" />
                <h3 className="font-bold text-sm">Guia do Chat & Atalhos Rápidos</h3>
              </div>
              <p className="text-xs text-indigo-200/90 leading-relaxed">
                Use atalhos inteligentes no meio das mensagens para linkar pedidos, produtos, marcar operadores e delegar tarefas na Sempre Joias.
              </p>
            </div>

            {/* CARD DESTACADO: CENTRAL DE TREINAMENTOS & POPS */}
            <div className="p-4 rounded-2xl bg-gradient-to-r from-indigo-50 to-blue-50 border-2 border-indigo-200 shadow-2xs space-y-2.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-bold text-indigo-950">
                  <GraduationCap size={18} className="text-indigo-600" />
                  <span>Central de Treinamentos & POPs</span>
                </div>
                <span className="px-2 py-0.5 rounded-md bg-indigo-600 text-white text-[10px] font-bold">
                  Novo
                </span>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                Consulte os manuais da empresa divididos nas etapas de <b>Atendimento & Vendas</b> e <b>Fábrica & Oficina</b>, com procedimentos operacionais (POPs) numerados e checklists.
              </p>
              <button
                type="button"
                onClick={() => {
                  if (onNavigateTab) {
                    onNavigateTab("treinamentos");
                    closeDrawer();
                  }
                }}
                className="w-full py-2 px-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold transition shadow-xs flex items-center justify-center gap-2 cursor-pointer"
              >
                <BookOpen size={14} />
                <span>Abrir Treinamentos da Empresa</span>
                <ChevronRight size={14} />
              </button>
            </div>

            {/* CARD 1: CITAR PEDIDOS */}
            <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-2.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-bold text-slate-800">
                  <Package size={16} className="text-indigo-600" />
                  <span>1. Citar Pedidos com Ficha Rápida</span>
                </div>
                <span className="font-mono text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-md font-bold">
                  [71740] ou #71740
                </span>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                Escreva o número do pedido entre colchetes como <code className="bg-slate-100 px-1.5 py-0.5 rounded text-indigo-700 font-bold">[71740]</code> no texto. O chat criará automaticamente uma pílula interativa.
              </p>
              <div className="p-3 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-between">
                <span className="text-[11px] text-slate-500">Exemplo interativo (clique para testar):</span>
                <button
                  type="button"
                  onClick={() => openQuickOrder("71740")}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold text-xs border border-indigo-200 transition shadow-xs cursor-pointer active:scale-95"
                >
                  <Package size={12} />
                  <span>#71740</span>
                </button>
              </div>
              <p className="text-[11px] text-slate-400">
                ✨ Abre a ficha com aro, pedras, gravação, foto e status de Nota Fiscal, Certificado e Etiqueta.
              </p>
            </div>

            {/* CARD 2: CITAR PRODUTOS (SKU) */}
            <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-2.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-bold text-slate-800">
                  <Gem size={16} className="text-emerald-600" />
                  <span>2. Citar Produtos do Catálogo</span>
                </div>
                <span className="font-mono text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-md font-bold">
                  sku.DIR-TRI ou [DIR-TRI]
                </span>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                Escreva <code className="bg-slate-100 px-1.5 py-0.5 rounded text-emerald-700 font-bold">sku.DIR-TRI</code> ou <code className="bg-slate-100 px-1.5 py-0.5 rounded text-emerald-700 font-bold">[sku.DIR-TRI]</code> em qualquer canal ou DM.
              </p>
              <div className="p-3 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-between">
                <span className="text-[11px] text-slate-500">Exemplo interativo (clique para testar):</span>
                <button
                  type="button"
                  onClick={() => openQuickProduct("DIR-TRI")}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-bold text-xs border border-emerald-200 transition shadow-xs cursor-pointer active:scale-95"
                >
                  <Gem size={12} className="text-emerald-600" />
                  <span>DIR-TRI</span>
                </button>
              </div>
              <p className="text-[11px] text-slate-400">
                ✨ Carrega foto ampliada, preço, categoria e botão para ir direto para a Ficha do Produto.
              </p>
            </div>

            {/* CARD 3: MARCAR OPERADORES E USUÁRIOS */}
            <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-2.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-bold text-slate-800">
                  <AtSign size={16} className="text-blue-600" />
                  <span>3. Marcar Colaboradores & Operadores</span>
                </div>
                <span className="font-mono text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-md font-bold">
                  @nome
                </span>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                Digite <code className="bg-slate-100 px-1.5 py-0.5 rounded text-blue-700 font-bold">@</code> para abrir a lista de pessoas. Suporta tanto usuários principais quanto operadores de bancada/PDV, enviando notificação individual para quem foi chamado.
              </p>
            </div>

            {/* CARD 4: ASSISTENTE DE IA COMPANION */}
            <div className="p-4 rounded-2xl bg-gradient-to-br from-indigo-50/90 via-purple-50/40 to-white border border-indigo-200/90 shadow-xs space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-bold text-indigo-950">
                  <Sparkles size={16} className="text-indigo-600" />
                  <span>4. Assistente de IA Companion ✨ (Capacidades e Limites)</span>
                </div>
                <span className="font-mono text-[10px] bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-md font-bold border border-indigo-200">
                  @ia ou Chat da IA
                </span>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                O assistente é conectado diretamente ao banco de dados para responder consultas operacionais rápidas de forma <b>100% determinística, segura e sem alucinações</b>.
              </p>

              {/* SEÇÃO 1: O QUE A IA PODE FAZER */}
              <div className="space-y-2 pt-1">
                <div className="flex items-center gap-1.5 text-[11px] font-bold text-emerald-700 uppercase tracking-wider">
                  <Check size={14} className="text-emerald-600" />
                  <span>O que a IA PODE fazer (Exemplos Interativos):</span>
                </div>

                <div className="grid grid-cols-1 gap-2 text-xs">
                  {/* EXEMPLO 1: FÁBRICA / KANBAN */}
                  <button
                    type="button"
                    onClick={() => {
                      startAIChat();
                      setInputText("Como está a produção do pedido [71740], quais os aros e o que está anotado no mural?");
                    }}
                    className="p-2.5 rounded-xl bg-white border border-emerald-100 hover:border-emerald-300 text-left transition shadow-2xs cursor-pointer flex items-start gap-2.5 group"
                  >
                    <span className="text-base leading-none shrink-0 mt-0.5">🏭</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-slate-800 group-hover:text-emerald-700 transition">
                        "Como está a produção do pedido [71740] e quais os aros?"
                      </p>
                      <span className="text-[10px] text-slate-400 block mt-0.5">
                        Traz dados reais da bancada: peças, aros, pedras, gravação na joia, etapa do Kanban e mural.
                      </span>
                    </div>
                  </button>

                  {/* EXEMPLO 2: LOGÍSTICA / ENVIO */}
                  <button
                    type="button"
                    onClick={() => {
                      startAIChat();
                      setInputText("Qual o rastreio e status logístico do pedido [71740]?");
                    }}
                    className="p-2.5 rounded-xl bg-white border border-emerald-100 hover:border-emerald-300 text-left transition shadow-2xs cursor-pointer flex items-start gap-2.5 group"
                  >
                    <span className="text-base leading-none shrink-0 mt-0.5">🚚</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-slate-800 group-hover:text-emerald-700 transition">
                        "Qual o rastreio e status logístico do pedido [71740]?"
                      </p>
                      <span className="text-[10px] text-slate-400 block mt-0.5">
                        Consulta código de rastreamento dos Correios, destinatário, cidade/UF e NF-e emitida.
                      </span>
                    </div>
                  </button>

                  {/* EXEMPLO 3: DIAS ÚTEIS */}
                  <button
                    type="button"
                    onClick={() => {
                      startAIChat();
                      setInputText("Quais pedidos estão no quarto dia útil?");
                    }}
                    className="p-2.5 rounded-xl bg-white border border-emerald-100 hover:border-emerald-300 text-left transition shadow-2xs cursor-pointer flex items-start gap-2.5 group"
                  >
                    <span className="text-base leading-none shrink-0 mt-0.5">📅</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-slate-800 group-hover:text-emerald-700 transition">
                        "Quais pedidos estão no quarto dia útil?"
                      </p>
                      <span className="text-[10px] text-slate-400 block mt-0.5">
                        Calcula a idade em dias úteis de fábrica e lista pedidos no prazo ou atrasados.
                      </span>
                    </div>
                  </button>

                  {/* EXEMPLO 4: BUSCA POR CLIENTE */}
                  <button
                    type="button"
                    onClick={() => {
                      startAIChat();
                      setInputText("Encontre no arquivo morto o pedido do Luiz Otavio para mim");
                    }}
                    className="p-2.5 rounded-xl bg-white border border-emerald-100 hover:border-emerald-300 text-left transition shadow-2xs cursor-pointer flex items-start gap-2.5 group"
                  >
                    <span className="text-base leading-none shrink-0 mt-0.5">👤</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-slate-800 group-hover:text-emerald-700 transition">
                        "Encontre no arquivo morto o pedido do Luiz Otavio"
                      </p>
                      <span className="text-[10px] text-slate-400 block mt-0.5">
                        Localiza pedidos ativos ou arquivados através do nome do cliente.
                      </span>
                    </div>
                  </button>

                  {/* EXEMPLO 5: PRODUTO POR SKU */}
                  <button
                    type="button"
                    onClick={() => {
                      startAIChat();
                      setInputText("Qual o preço e detalhes do produto sku.DIR-TRI?");
                    }}
                    className="p-2.5 rounded-xl bg-white border border-emerald-100 hover:border-emerald-300 text-left transition shadow-2xs cursor-pointer flex items-start gap-2.5 group"
                  >
                    <span className="text-base leading-none shrink-0 mt-0.5">💎</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-slate-800 group-hover:text-emerald-700 transition">
                        "Qual o preço e detalhes do produto sku.DIR-TRI?"
                      </p>
                      <span className="text-[10px] text-slate-400 block mt-0.5">
                        Consulta oficial do catálogo por SKU: nome da joia, preço de venda e categoria.
                      </span>
                    </div>
                  </button>
                </div>
              </div>

              {/* SEÇÃO 2: O QUE A IA NÃO PODE FAZER */}
              <div className="space-y-2 pt-2 border-t border-indigo-100">
                <div className="flex items-center gap-1.5 text-[11px] font-bold text-amber-700 uppercase tracking-wider">
                  <AlertCircle size={14} className="text-amber-600" />
                  <span>O que a IA NÃO pode fazer (E onde realizar):</span>
                </div>

                <div className="space-y-1.5 text-xs text-slate-600">
                  <div className="p-2.5 rounded-xl bg-amber-50/60 border border-amber-200/80 space-y-1">
                    <p className="font-bold text-amber-900 flex items-center gap-1.5">
                      <span>❌ Consultar saldo geral de estoque físico por texto vago</span>
                    </p>
                    <p className="text-[11px] text-slate-600 leading-relaxed">
                      Devido às centenas de cursos, modelos e aros da fábrica, a IA não faz buscas amplas sem SKU para evitar divergências.
                    </p>
                    <p className="text-[11px] text-amber-800 font-semibold">
                      👉 Onde consultar: utilize a <b>Aba de Estoque</b> para verificar saldos físicos e medidas com precisão.
                    </p>
                  </div>

                  <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200 space-y-1">
                    <p className="font-bold text-slate-800 flex items-center gap-1.5">
                      <span>❌ Movimentar pedidos no Kanban ou alterar etapas</span>
                    </p>
                    <p className="text-[11px] text-slate-600">
                      👉 Utilize os cards nas abas de <b>Operações / Produção</b> para avançar etapas.
                    </p>
                  </div>

                  <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200 space-y-1">
                    <p className="font-bold text-slate-800 flex items-center gap-1.5">
                      <span>❌ Gerar etiquetas dos Correios ou emitir NF-e</span>
                    </p>
                    <p className="text-[11px] text-slate-600">
                      👉 Utilize a aba de <b>Postagem / Expedição</b> para gerar fretes e documentos fiscais.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* CARD 5: ATALHOS DE TECLADO */}
            <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-3">
              <div className="flex items-center gap-2 text-xs font-bold text-slate-800">
                <Keyboard size={16} className="text-indigo-600" />
                <span>5. Atalhos de Teclado & Produtividade</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-between">
                  <span className="text-slate-600">Enviar Mensagem</span>
                  <kbd className="px-2 py-0.5 bg-white border border-slate-200 rounded font-mono text-[10px] font-bold shadow-2xs">
                    Enter
                  </kbd>
                </div>

                <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-between">
                  <span className="text-slate-600">Nova Linha (Pular)</span>
                  <kbd className="px-2 py-0.5 bg-white border border-slate-200 rounded font-mono text-[10px] font-bold shadow-2xs">
                    Shift + Enter
                  </kbd>
                </div>

                <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-between">
                  <span className="text-slate-600">Colar Print / Foto</span>
                  <kbd className="px-2 py-0.5 bg-white border border-slate-200 rounded font-mono text-[10px] font-bold shadow-2xs">
                    Ctrl + V
                  </kbd>
                </div>

                <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-between">
                  <span className="text-slate-600">Mencionar Usuário / IA</span>
                  <kbd className="px-2 py-0.5 bg-white border border-slate-200 rounded font-mono text-[10px] font-bold shadow-2xs">
                    @
                  </kbd>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ================= 5. CONTEÚDO DA TAB PREFERÊNCIAS ================= */}
        {activeDrawerTab === "preferences" && (
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6 bg-slate-50">
            {/* CABEÇALHO DA TAB */}
            <div className="p-4 rounded-2xl bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white shadow-md flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Settings size={18} className="text-indigo-400" />
                  <h3 className="text-sm font-black tracking-wide">Preferências do Sistema & IA</h3>
                </div>
                <p className="text-xs text-slate-300">
                  Defina o motor de Inteligência Artificial principal para responder as consultas do chat.
                </p>
              </div>
            </div>

            {/* SEÇÃO: SELEÇÃO DE PROVEDOR DE IA */}
            <div className="p-5 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-bold text-slate-800">
                  <Sparkles size={16} className="text-indigo-600" />
                  <span>Assistente de IA Principal (Companion)</span>
                </div>
                {savingProvider && (
                  <span className="text-[11px] font-bold text-indigo-600 animate-pulse">
                    Salvando preferência...
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {availableAiProviders.map((p) => {
                  const isSelected = selectedAiProviderId === p.id;
                  const isDefaultActive = Boolean(p.ativo);

                  return (
                    <div
                      key={p.id}
                      onClick={() => handleSetDefaultAiProvider(p.id)}
                      className={`p-4 rounded-2xl border-2 transition cursor-pointer flex flex-col justify-between text-left relative overflow-hidden ${
                        isSelected
                          ? "border-indigo-600 bg-indigo-50/50 shadow-md ring-2 ring-indigo-500/20"
                          : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-xs"
                      }`}
                    >
                      {/* Selo de Principal */}
                      {isSelected && (
                        <div className="absolute top-2 right-2 flex items-center gap-1 bg-indigo-600 text-white text-[9px] font-black uppercase px-2 py-0.5 rounded-full shadow-2xs">
                          <Check size={10} strokeWidth={3} />
                          <span>Principal</span>
                        </div>
                      )}

                      <div className="space-y-2">
                        <div className="text-2xl">
                          {p.provedor === "openai" ? "🤖" : p.provedor === "google" ? "⚡" : "🔮"}
                        </div>
                        <div>
                          <h4 className="text-xs font-black text-slate-900">{p.nome}</h4>
                          <span className="text-[10px] font-mono text-slate-500 block mt-0.5">
                            Modelo: {p.modelo}
                          </span>
                        </div>
                      </div>

                      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px]">
                        <span
                          className={`font-bold ${
                            p.temChave ? "text-emerald-600" : "text-amber-600"
                          }`}
                        >
                          {p.temChave ? "✓ Chave Ativa" : "⚠️ Sem Chave"}
                        </span>

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSetDefaultAiProvider(p.id);
                          }}
                          className={`px-2.5 py-1 rounded-lg text-[10px] font-black transition ${
                            isSelected
                              ? "bg-indigo-600 text-white"
                              : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                          }`}
                        >
                          {isSelected ? "Ativo" : "Usar Este"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-600 flex items-center justify-between">
                <span>Deseja adicionar ou alterar suas chaves de API e modelos?</span>
                <span className="font-bold text-indigo-600 hover:underline">
                  Menu Superior &gt; Configurações &gt; Configurações de IA
                </span>
              </div>
            </div>

            {/* SEÇÃO: ALERTAS SONOROS NO NAVEGADOR */}
            <div className="p-5 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-bold text-slate-800">
                  {soundEnabled ? (
                    <Volume2 size={16} className="text-emerald-600" />
                  ) : (
                    <VolumeX size={16} className="text-slate-400" />
                  )}
                  <span>Alertas Sonoros no Navegador (Web Audio API)</span>
                </div>
                <button
                  type="button"
                  onClick={toggleSound}
                  className={`px-3 py-1 rounded-full text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
                    soundEnabled
                      ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                      : "bg-slate-100 text-slate-600 border border-slate-300"
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full ${soundEnabled ? "bg-emerald-500 animate-pulse" : "bg-slate-400"}`} />
                  {soundEnabled ? "Sons Ativados" : "Silenciado"}
                </button>
              </div>

              <p className="text-xs text-slate-500 leading-relaxed">
                Toca efeitos sonoros suaves e discretos quando novas mensagens chegam no chat, ou quando tarefas e menções forem direcionadas a você ou sua equipe.
              </p>

              {/* BOTÕES DE TESTE DE SOM */}
              <div className="pt-2 border-t border-slate-100 flex flex-wrap items-center gap-2">
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mr-1">
                  Testar Toques:
                </span>
                <button
                  type="button"
                  onClick={() => testSound("message")}
                  className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-indigo-50 hover:text-indigo-700 text-slate-700 text-xs font-bold transition flex items-center gap-1.5 border border-slate-200 hover:border-indigo-200 cursor-pointer"
                >
                  💬 Chat (2 tons)
                </button>
                <button
                  type="button"
                  onClick={() => testSound("notif")}
                  className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-purple-50 hover:text-purple-700 text-slate-700 text-xs font-bold transition flex items-center gap-1.5 border border-slate-200 hover:border-purple-200 cursor-pointer"
                >
                  🔔 Notificação (3 tons)
                </button>
                <button
                  type="button"
                  onClick={() => testSound("urgent")}
                  className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-rose-50 hover:text-rose-700 text-slate-700 text-xs font-bold transition flex items-center gap-1.5 border border-slate-200 hover:border-rose-200 cursor-pointer"
                >
                  🚨 Urgente
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ================= 6. MODAL DE NOVA CONVERSA DIRETA (DM) ================= */}
        {showDMSelector && (
          <div className="absolute inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-sm overflow-hidden flex flex-col max-h-[80vh]">
              <div className="p-4 bg-slate-900 text-white flex items-center justify-between">
                <span className="text-xs font-bold">Iniciar Mensagem Direta</span>
                <button
                  onClick={() => setShowDMSelector(false)}
                  className="text-slate-400 hover:text-white cursor-pointer"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="p-3 border-b border-slate-100">
                <input
                  type="text"
                  placeholder="Buscar usuário ou operador..."
                  value={dmSearch}
                  onChange={(e) => setDmSearch(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="flex-1 overflow-y-auto p-2 space-y-1">
                {/* USUÁRIOS */}
                <div className="px-2 py-1 text-[10px] font-bold text-slate-400 uppercase">
                  Usuários do Sistema
                </div>
                {usersList
                  .filter((u) => u.name?.toLowerCase().includes(dmSearch.toLowerCase()))
                  .map((u) => (
                    <button
                      key={u.id}
                      onClick={() => {
                        startDM({ targetUser: u });
                        setShowDMSelector(false);
                      }}
                      className="w-full px-2.5 py-2 rounded-xl hover:bg-slate-100 text-left flex items-center gap-2 text-xs font-medium cursor-pointer"
                    >
                      <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-[10px] font-bold">
                        <User size={12} />
                      </div>
                      <span className="truncate flex-1">{u.name || u.username}</span>
                    </button>
                  ))}

                {/* OPERADORES */}
                <div className="px-2 pt-3 pb-1 text-[10px] font-bold text-slate-400 uppercase border-t border-slate-100">
                  Operadores de Bancada / PDV
                </div>
                {operatorsList
                  .filter((op) => op.nome?.toLowerCase().includes(dmSearch.toLowerCase()))
                  .map((op) => (
                    <button
                      key={op.id}
                      onClick={() => {
                        startDM({ targetOperator: op });
                        setShowDMSelector(false);
                      }}
                      className="w-full px-2.5 py-2 rounded-xl hover:bg-slate-100 text-left flex items-center gap-2 text-xs font-medium cursor-pointer"
                    >
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold"
                        style={{ backgroundColor: op.cor || "#10b981" }}
                      >
                        <Wrench size={12} />
                      </div>
                      <span className="truncate flex-1">{op.nome}</span>
                    </button>
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* ================= 7. MODAL DE CRIAR NOVO CANAL ================= */}
        {showNewChannelModal && (
          <div className="absolute inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-sm overflow-hidden flex flex-col max-h-[85vh] animate-scaleUp">
              <div className="p-4 bg-slate-900 text-white flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Megaphone size={16} className="text-indigo-400" />
                  <span className="text-xs font-bold">Criar Novo Canal de Chat</span>
                </div>
                <button
                  onClick={() => setShowNewChannelModal(false)}
                  className="text-slate-400 hover:text-white cursor-pointer"
                >
                  <X size={16} />
                </button>
              </div>

              <form onSubmit={handleCreateChannelSubmit} className="p-4 space-y-3.5 overflow-y-auto">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                    Nome de Exibição do Canal *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Ex: Qualidade & Acabamento"
                    value={newChannelDisplayName}
                    onChange={(e) => setNewChannelDisplayName(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                    Área / Módulo Vinculado (Permissão)
                  </label>
                  <select
                    value={newChannelModule}
                    onChange={(e) => setNewChannelModule(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs font-medium bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {MODULE_OPTIONS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-[10px] text-slate-400 mt-1">
                    Quem tiver acesso a essa área terá acesso automático ao canal.
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                    Descrição do Canal
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: Discussões sobre padrão de polimento e cravação"
                    value={newChannelDesc}
                    onChange={(e) => setNewChannelDesc(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                    Ícone
                  </label>
                  <div className="grid grid-cols-4 gap-1.5">
                    {Object.keys(CHANNEL_ICONS).map((iconName) => {
                      const IconC = CHANNEL_ICONS[iconName];
                      const isSel = newChannelIcon === iconName;
                      return (
                        <button
                          key={iconName}
                          type="button"
                          onClick={() => setNewChannelIcon(iconName)}
                          className={`p-2 rounded-xl border flex items-center justify-center transition cursor-pointer ${
                            isSel
                              ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                              : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
                          }`}
                        >
                          <IconC size={16} />
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="pt-2 flex items-center justify-end gap-2 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setShowNewChannelModal(false)}
                    className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-lg cursor-pointer"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={creatingChannel}
                    className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-sm transition disabled:opacity-50 cursor-pointer"
                  >
                    {creatingChannel ? "Criando..." : "Salvar Canal"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
